import { useEffect, useMemo, useRef } from "react"
import { Line } from "react-chartjs-2"
import { useNavigate, useParams } from "react-router-dom"
import { Chart, ChartEvent, registerables, TooltipItem, TooltipModel } from "chart.js"
import dayjs from "dayjs"
import dayjs_plugin_timezone from "dayjs/plugin/timezone"
import styled from "styled-components"

import { useLazyGetMonitoringHistoricalDataQuery } from "store/api/monitoringApi"
import { useAppSelector } from "store/hooks"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import {
  borderColorWithSkipped,
  borderDashWithSkipped,
  calculateMinTemperatureValue,
  defaultChartTooltipOptions,
  fillMonitoringMissingHistoricalData,
} from "utils/chartUtils"
import { getRoundedTemperature } from "utils/getRoundedTemperature"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import parseCelsiusToFahrenheit from "utils/parseCelsiusToFahrenheit"
import { getMonitoringDateRangeParams } from "modals/StatsReportModal/getMonitoringDateRangeParams"
import getScheduleDataArray from "modals/StatsReportModal/getScheduleDataArray"
import ScheduleComponents from "modals/StatsReportModal/ScheduleComponents"
import { NoDataLayer } from "components/chart/NoDataLayer"
import { RestrictedAccessWidget } from "components/common/RestrictedAccessWidget"
import { Spinner } from "components/common/Spinner"
import { ButtonNew } from "components/form/ButtonNew"

import { useCustomerDashboard } from "../../CustomerDashboardContext"
import {
  ChartContainer,
  LegendColorBox,
  LegendContainer,
  LegendItem,
  LegendLabel,
  LoadingContainer,
  Widget,
} from "../styled"

dayjs.extend(dayjs_plugin_timezone)

Chart.register(...registerables)

const ChartBottomDiv = styled.div`
  position: absolute;
  height: 24px;
  opacity: 0.7;
  z-index: 1;
`

const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 16px 0;
  padding-bottom: 13px;
  border-bottom: 1px solid #e2e8f0;
`

const WidgetTitleText = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  margin: 0;
`

const SeeDetailsButtonWrapper = styled(ButtonNew)`
  font-size: 12px;
  height: 32px;
  color: #334155 !important;
  border: 1px solid #e2e8f0 !important;
`

export const TemperatureVsSetpointWidget = () => {
  const { devices, currentDevice } = useCustomerDashboard()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const userTemperatureScale = useAppSelector(state => state.user.userTemperatureScale)
  const isCelsius = userTemperatureScale === "celsius"
  const [triggerGetMonitoringHistoricalData, { data, isFetching }] = useLazyGetMonitoringHistoricalDataQuery()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasMonitoringAccess = userProfile ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.Monitoring) : false
  const selectedDevice = devices[currentDevice]
  const timeZone = selectedDevice?.time_zone_text || "UTC"

  const handleSeeDetails = () => {
    navigate(`/customers/${id}/monitoring`)
  }

  const dateRangeParams = useMemo(() => {
    return getMonitoringDateRangeParams("last24Hours", null, timeZone)
  }, [timeZone])

  const { settings } = dateRangeParams

  useEffect(() => {
    if (!selectedDevice?.sn || !hasMonitoringAccess) return

    const { startDateString, endDateString, intervalInMinutes } = dateRangeParams

    triggerGetMonitoringHistoricalData({
      sn: selectedDevice.sn,
      start_date_time: startDateString,
      end_date_time: endDateString,
      interval_in_minutes: intervalInMinutes,
    })
  }, [selectedDevice?.sn, triggerGetMonitoringHistoricalData, dateRangeParams])

  const { startDateTime, intervalInMinutes, itemsCount } = dateRangeParams

  const filledData = useMemo(() => {
    if (!data) return []

    const buckets = data?.aggregations?.["date_histogram#aggregation_over_time"]?.buckets || []
    return fillMonitoringMissingHistoricalData(buckets, startDateTime, intervalInMinutes, itemsCount)
  }, [data, startDateTime, intervalInMinutes, itemsCount])

  const parsedData = useMemo(() => {
    if (!filledData || filledData.length === 0) return []

    const getTemperature = (value: number | null) => {
      if (value === null) {
        return null
      }

      return isCelsius ? value : parseCelsiusToFahrenheit(value)
    }

    return filledData.map(item => ({
      temperature: getTemperature(item["avg#average_current_temperature_embedded"].value),
      setTemperature: getTemperature(item["avg#average_set_temperature"].value),
      timestamp: item.key_as_string,
    }))
  }, [filledData, isCelsius])

  const noData = parsedData.length === 0
  const temperatureMark = isCelsius ? "°C" : "°F"

  const currentTemperatureList = parsedData.map(item => item.temperature)
  const setTemperatureList = parsedData.map(item => item.setTemperature)

  const containerRef = useRef<HTMLDivElement>(null)
  const originDivRef = useRef<HTMLDivElement | null>(null)
  const showSchedule = true // Always true as per user requirement

  // Store timestamps for callback access
  const timestampsRef = useRef<string[]>([])
  timestampsRef.current = parsedData.map(item => item.timestamp)

  const scheduleData = useMemo(() => {
    if (!showSchedule) return []

    return getScheduleDataArray(filledData, intervalInMinutes)
  }, [filledData, showSchedule, intervalInMinutes])

  const minTemperatureValue = useMemo(() => {
    if (!showSchedule) {
      return null
    }

    const allTemperatures: (number | null)[] = []
    parsedData.forEach(item => {
      if (item.temperature !== null) allTemperatures.push(item.temperature)
      if (item.setTemperature !== null) allTemperatures.push(item.setTemperature)
    })

    return calculateMinTemperatureValue(allTemperatures, isCelsius)
  }, [parsedData, isCelsius, showSchedule])

  const chartData = {
    labels: [...parsedData.map((_, index) => index)],
    datasets: [
      {
        label: "Current temperature",
        data: currentTemperatureList,
        borderColor: "#43E0F8",
        pointBackgroundColor: "#43E0F8",
        pointStyle: false as unknown as "circle",
        borderWidth: 2,
        spanGaps: true,
        segment: {
          borderDash: borderDashWithSkipped,
          borderColor: borderColorWithSkipped,
        },
      },
      {
        label: "Set temperature",
        data: setTemperatureList,
        borderColor: "#22c55e",
        pointBackgroundColor: "#22c55e",
        pointStyle: false as unknown as "circle",
        borderWidth: 2,
        spanGaps: true,
        segment: {
          borderDash: borderDashWithSkipped,
          borderColor: borderColorWithSkipped,
        },
      },
      ...(parsedData.length === 0
        ? [
            {
              label: "",
              data: [74, 75, 76, 77, 78],
            },
          ]
        : []),
    ],
  }

  const chartOptions = {
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        ...defaultChartTooltipOptions,
        displayColors: true,
        callbacks: {
          title: function (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">[]) {
            const index = typeof tooltipItem[0].label === "number" ? tooltipItem[0].label : Number(tooltipItem[0].label)
            const timestamp = timestampsRef.current[index]
            if (timestamp) {
              const date = dayjs(timestamp).tz(timeZone || "UTC")
              return date.format("LLL")
            }
            return ""
          },
          label: function (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">) {
            return `${getRoundedTemperature(tooltipItem.formattedValue, 2)}${temperatureMark}`
          },
        },
      },
    },
    scales: {
      x: {
        border: {
          color: "#e2e8f0",
          dash: [4, 4],
        },
        grid: {
          display: true,
          color: "#e2e8f0",
          lineWidth: 1,
          drawTicks: false,
        },
        ticks: {
          maxRotation: 0,
          maxTicksLimit: 12,
          color: "#94A3B8",
          font: {
            size: 12,
          },
          callback: (value: number | string) => {
            // Divide by dataCountsDivider to get hours (0, 1, 2, 3, ...)
            const hour = Math.floor(Number(value) / settings.dataCountsDivider)
            // Only show even hours
            if (hour % 2 === 0) {
              return hour.toString()
            }
            return ""
          },
        },
      },
      y: {
        position: "left" as const,
        min: minTemperatureValue !== null && scheduleData.length > 0 ? minTemperatureValue : undefined,
        border: {
          display: false,
          color: "rgba(0,0,0,0)",
        },
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
    },
    onHover: (ctx: ChartEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const chart = (ctx as any).chart
      if (chart && chart.data && chart.data.datasets) {
        chart.data.datasets[0].pointStyle = "circle"
        chart.data.datasets[1].pointStyle = "circle"
      }
    },
    onLeave: (ctx: ChartEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const chart = (ctx as any).chart
      if (chart && chart.data && chart.data.datasets) {
        chart.data.datasets[0].pointStyle = false
        chart.data.datasets[1].pointStyle = false
      }
    },
  }

  const originOverlayPlugin = useMemo(
    () => ({
      id: "originOverlay",
      afterLayout: (chart: Chart) => {
        const area = chart.chartArea
        const div = originDivRef.current
        const container = containerRef.current
        if (!area || !div || !container) return
        div.style.left = `${area.left}px`
        div.style.width = `${area.right - area.left}px`
        div.style.top = `${area.bottom - div.offsetHeight}px`
      },
    }),
    [],
  )

  if (!selectedDevice) {
    return null
  }

  return (
    <Widget>
      <WidgetHeader>
        <WidgetTitleText>Temperature vs. Setpoint</WidgetTitleText>
        {hasMonitoringAccess && (
          <SeeDetailsButtonWrapper onClick={handleSeeDetails} color="greyBordered" size="auto">
            See Details
          </SeeDetailsButtonWrapper>
        )}
      </WidgetHeader>
      <RestrictedAccessWidget privilege={SubscriptionPrivilege.Monitoring}>
        {isFetching ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : (
          <>
            <ChartContainer
              ref={containerRef}
              style={{ height: showSchedule && scheduleData.length > 0 ? "164px" : "140px" }}
            >
              {noData && <NoDataLayer />}
              <Line data={chartData} options={chartOptions} plugins={[originOverlayPlugin]} />
              {showSchedule && scheduleData.length > 0 && (
                <ChartBottomDiv ref={originDivRef}>
                  <ScheduleComponents
                    overallDocsCount={filledData.length * intervalInMinutes}
                    scheduleData={scheduleData}
                  />
                </ChartBottomDiv>
              )}
            </ChartContainer>
            {!noData && (
              <LegendContainer>
                <LegendItem>
                  <LegendColorBox $color="#43E0F8" />
                  <LegendLabel>Current temperature</LegendLabel>
                </LegendItem>
                <LegendItem>
                  <LegendColorBox $color="#22c55e" />
                  <LegendLabel>Set temperature</LegendLabel>
                </LegendItem>
              </LegendContainer>
            )}
          </>
        )}
      </RestrictedAccessWidget>
    </Widget>
  )
}
